import { z } from 'zod'
import { zodDate } from '../utilities/zodDate'

import { notificationProperties } from './BaseModels'
import cachedDummies from './cachedDummies'
import parseModel from './parseModel'

/**********************************************************************
 * UserCreatedNotification
 **********************************************************************/
const userCreatedNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserCreated'),
  data: z.object({
    courseUuidIntroduction: z.string(),
  }),
})

export type UserCreatedNotification = z.infer<typeof userCreatedNotification>

/**********************************************************************
 * UserAddedToGroupNotification
 **********************************************************************/
const userAddedToGroupNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserAddedToGroup'),
  data: z.object({
    group: z.string(),
    actionUser: z.string(),
  }),
})

export type UserAddedToGroupNotification = z.infer<typeof userAddedToGroupNotification>

/**********************************************************************
 * UserRemovedFromGroup
 **********************************************************************/
const userRemovedFromGroupNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserRemovedFromGroup'),
  data: z.object({
    group: z.string(),
    actionUser: z.string(),
  }),
})

export type UserRemovedFromGroupNotification = z.infer<typeof userRemovedFromGroupNotification>

/**********************************************************************
 * UserAddedAsManagerToGroup
 **********************************************************************/
const userAddedAsManagerToGroupNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserAddedAsManagerToGroup'),
  data: z.object({
    group: z.string(),
    actionUser: z.string(),
  }),
})

export type UserAddedAsManagerToGroupNotification = z.infer<typeof userAddedAsManagerToGroupNotification>

/**********************************************************************
 * UserRemovedAsManagerFromGroup
 **********************************************************************/
const userRemovedAsManagerFromGroupNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserRemovedAsManagerFromGroup'),
  data: z.object({
    group: z.string(),
    actionUser: z.string(),
  }),
})

export type UserRemovedAsManagerFromGroupNotification = z.infer<typeof userRemovedAsManagerFromGroupNotification>

/**********************************************************************
 * UserAddedLearningSuggestion
 **********************************************************************/
const userAddedLearningSuggestionNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserAddedLearningSuggestion'),
  data: z.object({
    learningItem: z.string(),
    actionUser: z.string(),
  }),
})

export type UserAddedLearningSuggestionNotification = z.infer<typeof userAddedLearningSuggestionNotification>

/**********************************************************************
 * UserAddedLearningAssignment
 **********************************************************************/
const userAddedLearningAssignmentNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserAddedLearningAssignment'),
  data: z.object({
    learningItem: z.string(),
    actionUser: z.string(),
    dueDate: zodDate(),
  }),
})

export type UserAddedLearningAssignmentNotification = z.infer<typeof userAddedLearningAssignmentNotification>

/**********************************************************************
 * UserAddedLearningAssignmentWithdrawn
 **********************************************************************/
const userAddedLearningAssignmentWithdrawnNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserAddedLearningAssignmentWithdrawn'),
  data: z.object({
    learningItem: z.string(),
    actionUser: z.string(),
  }),
})

export type UserAddedLearningAssignmentWithdrawnNotification = z.infer<
  typeof userAddedLearningAssignmentWithdrawnNotification
>

/**********************************************************************
 * UserAddedLearningAssignmentLiveEvent
 **********************************************************************/
const userAddedLearningAssignmentLiveEventNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserAddedLearningAssignmentLiveEvent'),
  data: z.object({
    learningItem: z.string(),
    actionUser: z.string(),
    booking: z.string(),
    event: z.string(),
  }),
})

export type UserAddedLearningAssignmentLiveEventNotification = z.infer<
  typeof userAddedLearningAssignmentLiveEventNotification
>

/**********************************************************************
 * GroupAddedLearningSuggestion
 **********************************************************************/
const groupAddedLearningSuggestionNotification = z.object({
  ...notificationProperties,
  type: z.literal('GroupAddedLearningSuggestion'),
  data: z.object({
    learningItem: z.string(),
    actionUser: z.string(),
    group: z.string(),
  }),
})

export type GroupAddedLearningSuggestionNotification = z.infer<typeof groupAddedLearningSuggestionNotification>

/**********************************************************************
 * GroupAddedLearningAssignment
 **********************************************************************/
const groupAddedLearningAssignmentNotification = z.object({
  ...notificationProperties,
  type: z.literal('GroupAddedLearningAssignment'),
  data: z.object({
    learningItem: z.string(),
    actionUser: z.string(),
    dueDate: zodDate(),
    group: z.string(),
  }),
})

export type GroupAddedLearningAssignmentNotification = z.infer<typeof groupAddedLearningAssignmentNotification>

/**********************************************************************
 * UserUpcomingEvent3Days
 **********************************************************************/
const userUpcomingEvent3DaysNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserUpcomingEvent3Days'),
  data: z.object({
    event: z.string(),
    learningItem: z.string(),
    date: zodDate(),
  }),
})

export type UserUpcomingEvent3DaysNotification = z.infer<typeof userUpcomingEvent3DaysNotification>

/**********************************************************************
 * UserUpcomingEvent
 **********************************************************************/
const userUpcomingEventNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserUpcomingEvent'),
  data: z.object({
    event: z.string(),
    learningItem: z.string(),
    date: zodDate(),
  }),
})

export type UserUpcomingEventNotification = z.infer<typeof userUpcomingEventNotification>

/**********************************************************************
 * CourseFinished
 **********************************************************************/
const courseFinishedNotification = z.object({
  ...notificationProperties,
  type: z.literal('CourseFinished'),
  data: z.object({
    learningItem: z.string(),
    userUuid: z.string(),
    withRatingLink: z.boolean(),
    ratingLink: z.string(),
  }),
})

export type CourseFinishedNotification = z.infer<typeof courseFinishedNotification>

/**********************************************************************
 * UserLearningAssignmentDue7Days
 **********************************************************************/
const userLearningAssignmentDue7DaysNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserLearningAssignmentDue7Days'),
  data: z.object({
    assignment: z.string(),
    learningItem: z.string(),
    dueDate: zodDate(),
  }),
})

export type UserLearningAssignmentDue7DaysNotification = z.infer<typeof userLearningAssignmentDue7DaysNotification>

/**********************************************************************
 * UserLearningAssignmentDue3Days
 **********************************************************************/
const userLearningAssignmentDue3DaysNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserLearningAssignmentDue3Days'),
  data: z.object({
    assignment: z.string(),
    learningItem: z.string(),
    dueDate: zodDate(),
  }),
})

export type UserLearningAssignmentDue3DaysNotification = z.infer<typeof userLearningAssignmentDue3DaysNotification>

/**********************************************************************
 * UserLearningAssignmentOverdue
 **********************************************************************/
const userLearningAssignmentOverdueNotification = z.object({
  ...notificationProperties,
  type: z.literal('UserLearningAssignmentOverdue'),
  data: z.object({
    assignment: z.string(),
    learningItem: z.string(),
    dueDate: zodDate(),
  }),
})

export type UserLearningAssignmentOverdueNotification = z.infer<typeof userLearningAssignmentOverdueNotification>

/**********************************************************************
 * ManagerUserFinishedLearningAssignment
 **********************************************************************/
const managerUserFinishedLearningAssignmentNotification = z.object({
  ...notificationProperties,
  type: z.literal('ManagerUserFinishedLearningAssignment'),
  data: z.object({
    group: z.string(),
    actionUser: z.string(),
    learningItem: z.string(),
  }),
})

export type ManagerUserFinishedLearningAssignmentNotification = z.infer<
  typeof managerUserFinishedLearningAssignmentNotification
>

/**********************************************************************
 * ManagerLearningAssignmentOverdue
 **********************************************************************/
const managerLearningAssignmentPersonDataSchema = z.object({
  dueDate: zodDate(),
  userFullName: z.string(),
})

const managerLearningAssignmentOverdueNotification = z.object({
  ...notificationProperties,
  type: z.literal('ManagerLearningAssignmentOverdue'),
  data: z.object({
    groupUuid: z.string(),
    groupData: z.record(
      z.string(), // Key: learningItem->title
      z.array(managerLearningAssignmentPersonDataSchema)
    ),
  }),
})

export type ManagerLearningAssignmentOverdueNotification = z.infer<typeof managerLearningAssignmentOverdueNotification>

/**********************************************************************
 * ManagerGroupFinishedLearningAssignment
 **********************************************************************/
const managerGroupFinishedLearningAssignmentNotification = z.object({
  ...notificationProperties,
  type: z.literal('ManagerGroupFinishedLearningAssignment'),
  data: z.object({
    group: z.string(),
    learningItem: z.string(),
  }),
})

export type ManagerGroupFinishedLearningAssignmentNotification = z.infer<
  typeof managerGroupFinishedLearningAssignmentNotification
>

/**********************************************************************
 * ManagerGroupLearningAssignmentOverdue
 **********************************************************************/
const managerGroupLearningAssignmentOverdueNotification = z.object({
  ...notificationProperties,
  type: z.literal('ManagerGroupLearningAssignmentOverdue'),
  data: z.object({
    assignment: z.string(),
    learningItem: z.string(),
    dueDate: zodDate(),
    completionRate: z.number(),
    group: z.string(),
  }),
})

export type ManagerGroupLearningAssignmentOverdueNotification = z.infer<
  typeof managerGroupLearningAssignmentOverdueNotification
>

/**********************************************************************
 * CertificateAvailable
 **********************************************************************/
const certificateAvailableNotification = z.object({
  ...notificationProperties,
  type: z.literal('CertificateAvailable'),
  data: z.object({
    learningItem: z.string(),
  }),
})

export type CertificateAvailableNotification = z.infer<typeof certificateAvailableNotification>

/**********************************************************************
 * CertificateInvalidation60Days
 **********************************************************************/
const certificateInvalidation60DaysNotification = z.object({
  ...notificationProperties,
  type: z.literal('CertificateInvalidation60Days'),
  data: z.object({
    certificate: z.string(),
    learningItem: z.string(),
    validUntil: zodDate(),
  }),
})

export type CertificateInvalidation60DaysNotification = z.infer<typeof certificateInvalidation60DaysNotification>

/**********************************************************************
 * CertificateInvalidation
 **********************************************************************/
const certificateInvalidationNotification = z.object({
  ...notificationProperties,
  type: z.literal('CertificateInvalidation'),
  data: z.object({
    certificate: z.string(),
    learningItem: z.string(),
    validUntil: zodDate(),
  }),
})

export type CertificateInvalidationNotification = z.infer<typeof certificateInvalidationNotification>

/**********************************************************************
 * ManagerCertificateInvalidation60Days
 **********************************************************************/
const managerCertificateInvalidation60DaysNotification = z.object({
  ...notificationProperties,
  type: z.literal('ManagerCertificateInvalidation60Days'),
  data: z.object({
    certificate: z.string(),
    learningItem: z.string(),
    validUntil: zodDate(),
    group: z.string(),
    user: z.string(),
  }),
})

export type ManagerCertificateInvalidation60DaysNotification = z.infer<
  typeof managerCertificateInvalidation60DaysNotification
>

/**********************************************************************
 * ManagerCertificateInvalidation
 **********************************************************************/
const managerCertificateInvalidationNotification = z.object({
  ...notificationProperties,
  type: z.literal('ManagerCertificateInvalidation'),
  data: z.object({
    certificate: z.string(),
    learningItem: z.string(),
    validUntil: zodDate(),
    group: z.string(),
    user: z.string(),
  }),
})

export type ManagerCertificateInvalidationNotification = z.infer<typeof managerCertificateInvalidationNotification>

/**********************************************************************
 * ScheduledCourseChanged
 **********************************************************************/
const scheduledCourseChangedNotification = z.object({
  ...notificationProperties,
  type: z.literal('ScheduledCourseChanged'),
  data: z.object({
    learningItem: z.string(),
  }),
})

export type ScheduledCourseChangedNotification = z.infer<typeof scheduledCourseChangedNotification>

/**********************************************************************
 * CourseAttachmentAvailable
 **********************************************************************/
const courseAttachmentAvailableNotification = z.object({
  ...notificationProperties,
  type: z.literal('CourseAttachmentAvailable'),
  data: z.object({
    learningItem: z.string(),
  }),
})

export type CourseAttachmentAvailableNotification = z.infer<typeof courseAttachmentAvailableNotification>

/**********************************************************************
 * ScheduledCourseDeleted
 **********************************************************************/
const scheduledCourseEventDeletedNotification = z.object({
  ...notificationProperties,
  type: z.literal('ScheduledCourseEventDeleted'),
  data: z.object({
    learningItemUuid: z.string(),
    learningItemTitle: z.string(),
  }),
})

export type ScheduledCourseEventDeletedNotification = z.infer<typeof scheduledCourseEventDeletedNotification>

/**********************************************************************
 * TopicAddedInCourseDiscussion
 **********************************************************************/
const topicAddedInCourseDiscussionNotification = z.object({
  ...notificationProperties,
  type: z.literal('TopicAddedInCourseDiscussion'),
  data: z.object({
    learningItemTitle: z.string(),
    learningItemUuid: z.string().uuid(),
    title: z.string(),
    text: z.string(),
    author: z.string(),
    authorUuid: z.string().uuid(),
    level: z.literal(1),
    topicUuid: z.string(),
  }),
})

export type TopicAddedInCourseDiscussionNotification = z.infer<typeof topicAddedInCourseDiscussionNotification>

/**********************************************************************
 * CommentAddedInCourseDiscussion
 **********************************************************************/
const commentAddedInCourseDiscussionNotification = z.object({
  ...notificationProperties,
  type: z.literal('CommentAddedInCourseDiscussion'),
  data: z.object({
    learningItemTitle: z.string(),
    learningItemUuid: z.string().uuid(),
    parentText: z.string(),
    parentAuthor: z.string(),
    text: z.string(),
    author: z.string(),
    authorUuid: z.string().uuid(),
    level: z.union([z.literal(2), z.literal(3)]),
    topicUuid: z.string().uuid(),
  }),
})

export type CommentAddedInCourseDiscussionNotification = z.infer<typeof commentAddedInCourseDiscussionNotification>

const courseRemovedFromLearningPathNotification = z.object({
  ...notificationProperties,
  type: z.literal('CourseRemovedFromLearningPath'),
  data: z.object({
    learningItemAsPath: z.string().uuid(),
    learningItemAsPathTitle: z.string(),
    deletedLearningItemsTitles: z.array(z.string()),
    userFullName: z.string(),
    userUuid: z.string().uuid(),
  }),
})

export type CourseRemovedFromLearningPathNotification = z.infer<typeof courseRemovedFromLearningPathNotification>

const courseAddedToLearningPathNotification = z.object({
  ...notificationProperties,
  type: z.literal('CourseAddedToLearningPath'),
  data: z.object({
    learningItemAsPath: z.string().uuid(),
    learningItemAsPathTitle: z.string(),
    addedLearningItemsTitles: z.array(z.string()),
    userFullName: z.string(),
    userUuid: z.string().uuid(),
  }),
})

export type CourseAddedToLearningPathNotification = z.infer<typeof courseAddedToLearningPathNotification>

/**********************************************************************
 * UnknownNotification
 **********************************************************************/
const unknownNotificationParser = z.object({
  ...notificationProperties,
  type: z.literal('Unknown'),
  data: z.any(),
  originalType: z.string(),
})

export type UnknownNotification = z.infer<typeof unknownNotificationParser>

/**********************************************************************
 * Notification
 **********************************************************************/
export const notificationParser = z.preprocess(
  (o: any) => {
    // The type coming from the Backend is like
    // `App\Notifications\CourseFinished`, sometimes with `Notification` at the
    // end and sometimes without. I don't want to deal with that at all.
    o.type = o.type
      .split('\\')
      .reverse()[0]
      .replace(/Notification$/, '')
    if (
      ![
        'UserCreated',
        'UserAddedToGroup',
        'UserRemovedFromGroup',
        'UserAddedAsManagerToGroup',
        'UserRemovedAsManagerFromGroup',
        'UserAddedLearningSuggestion',
        'UserAddedLearningAssignment',
        'UserAddedLearningAssignmentWithdrawn',
        'UserAddedLearningAssignmentLiveEvent',
        'GroupAddedLearningSuggestion',
        'GroupAddedLearningAssignment',
        'UserUpcomingEvent3Days',
        'UserUpcomingEvent',
        'CourseFinished',
        'UserLearningAssignmentDue7Days',
        'UserLearningAssignmentDue3Days',
        'UserLearningAssignmentOverdue',
        'ManagerUserFinishedLearningAssignment',
        'ManagerLearningAssignmentOverdue',
        'ManagerGroupFinishedLearningAssignment',
        'ManagerGroupLearningAssignmentOverdue',
        'CertificateAvailable',
        'CertificateInvalidation60Days',
        'CertificateInvalidation',
        'ManagerCertificateInvalidation60Days',
        'ManagerCertificateInvalidation',
        'ScheduledCourseChanged',
        'ScheduledCourseEventDeleted',
        'CourseAttachmentAvailable',
        'TopicAddedInCourseDiscussion',
        'CommentAddedInCourseDiscussion',
        'CourseRemovedFromLearningPath',
        'CourseAddedToLearningPath',
      ].includes(o.type)
    ) {
      o.originalType = o.type
      o.type = 'Unknown'
    }
    return o
  },
  z.discriminatedUnion('type', [
    userCreatedNotification,
    userAddedToGroupNotification,
    userRemovedFromGroupNotification,
    userAddedAsManagerToGroupNotification,
    userRemovedAsManagerFromGroupNotification,
    userAddedLearningSuggestionNotification,
    userAddedLearningAssignmentNotification,
    userAddedLearningAssignmentWithdrawnNotification,
    userAddedLearningAssignmentLiveEventNotification,
    groupAddedLearningSuggestionNotification,
    groupAddedLearningAssignmentNotification,
    userUpcomingEvent3DaysNotification,
    userUpcomingEventNotification,
    courseFinishedNotification,
    userLearningAssignmentDue7DaysNotification,
    userLearningAssignmentDue3DaysNotification,
    userLearningAssignmentOverdueNotification,
    managerUserFinishedLearningAssignmentNotification,
    managerLearningAssignmentOverdueNotification,
    managerGroupFinishedLearningAssignmentNotification,
    managerGroupLearningAssignmentOverdueNotification,
    certificateAvailableNotification,
    certificateInvalidation60DaysNotification,
    certificateInvalidationNotification,
    managerCertificateInvalidation60DaysNotification,
    managerCertificateInvalidationNotification,
    scheduledCourseChangedNotification,
    scheduledCourseEventDeletedNotification,
    courseAttachmentAvailableNotification,
    topicAddedInCourseDiscussionNotification,
    commentAddedInCourseDiscussionNotification,
    courseRemovedFromLearningPathNotification,
    courseAddedToLearningPathNotification,
    unknownNotificationParser,
  ])
)

export type Notification = z.infer<typeof notificationParser>

export const dummyNotification = cachedDummies(
  (uuid: string, error?: any): Notification => ({
    isDummy: true,
    error,

    uuid,

    type: 'Unknown',
    originalType: 'dummy',
    notifiableType: '',
    notifiableId: '',
    data: [],
    readAt: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  })
)

export const parseNotification = parseModel<Notification>(notificationParser, dummyNotification)
